/* Iter 418.aj (Dirk, 2026-02-28) — Full-page Plant Uptime detail.
   Replaces the previous SiteTimelineDrawer modal with a proper
   route (`/performance/plant-uptime/:siteId`) so users get room to
   see events, tickets, resolution notes, and to change the date
   range that drives both the on-screen view and the PDF download.
*/
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle, TrendingDown, TrendingUp, Clock, ChevronLeft,
  Download, LifeBuoy, MessageSquareText, CalendarDays, RefreshCw,
} from "lucide-react";
import api, { formatErr } from "../lib/api";

const PRESETS = [
  { key: "7d",   label: "7 days",   days: 7 },
  { key: "30d",  label: "30 days",  days: 30 },
  { key: "90d",  label: "90 days",  days: 90 },
  { key: "365d", label: "12 months", days: 365 },
];

function iso(d) { return d.toISOString(); }
function todayIsoDate() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${v.toFixed(2)}%`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16).replace("T", " "); }
}
function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
}
function uptimeAccent(pct) {
  if (pct >= 99.5) return "#10B981";
  if (pct >= 95) return "#F59E0B";
  return "#F43F5E";
}

export default function PlantUptimeDetail() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const preset = params.get("preset") || "30d";
  const fromParam = params.get("from") || "";
  const toParam = params.get("to") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  const { fromIso, toIso } = useMemo(() => {
    if (fromParam && toParam) {
      return {
        fromIso: iso(new Date(`${fromParam}T00:00:00Z`)),
        toIso: iso(new Date(`${toParam}T23:59:59Z`)),
      };
    }
    const p = PRESETS.find((x) => x.key === preset) || PRESETS[1];
    const from = new Date();
    from.setUTCDate(from.getUTCDate() - p.days);
    return { fromIso: iso(from), toIso: iso(new Date()) };
  }, [preset, fromParam, toParam]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: d } = await api.get(`/plant-sla/sites/${siteId}/timeline`, {
        params: { from: fromIso, to: toIso },
      });
      setData(d);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId, fromIso, toIso]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/plant-uptime/sites/${siteId}/pdf`, {
        params: { from: fromIso, to: toIso },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (data?.site_name || siteId).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 64);
      a.download = `HidroVerse_PlantUptime_${safe}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (e) {
      alert(formatErr(e) || "PDF download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const setPreset = (k) => {
    const next = new URLSearchParams(params);
    next.set("preset", k);
    next.delete("from");
    next.delete("to");
    setParams(next);
  };
  const setCustomRange = (from, to) => {
    const next = new URLSearchParams(params);
    if (from) next.set("from", from); else next.delete("from");
    if (to) next.set("to", to); else next.delete("to");
    next.delete("preset");
    setParams(next);
  };

  const stats = data?.stats || {};
  const events = data?.events || [];

  return (
    <div className="space-y-4" data-testid="plant-uptime-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hv-btn hv-btn-ghost text-xs mb-2"
            data-testid="plant-uptime-back"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">Plant Uptime · Site</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1 truncate">
            {data?.site_name || (loading ? "Loading…" : "Site")}
          </h1>
          {data?.customer_id && (
            <Link
              to={`/customers/${data.customer_id}`}
              className="text-[11px] text-[#22d3ee] hover:underline"
              data-testid="plant-uptime-customer-link"
            >
              Open customer →
            </Link>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="plant-uptime-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloading || loading || !data}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="plant-uptime-download-pdf"
            title="Download the Plant Uptime PDF for the selected date range"
          >
            <Download className={`w-3.5 h-3.5 ${downloading ? "animate-pulse" : ""}`} />
            {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Date range controls */}
      <div className="hv-card p-3 space-y-2" data-testid="plant-uptime-range">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] flex items-center gap-1">
            <CalendarDays className="w-3 h-3" /> Window
          </div>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(p.key)}
              className={`hv-btn hv-btn-ghost text-xs ${!fromParam && !toParam && preset === p.key ? "text-cyan-300 border-cyan-500/50" : ""}`}
              data-testid={`plant-uptime-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[#647087]">Or pick a custom range:</span>
          <input
            type="date"
            value={fromParam || daysAgoIso(30)}
            max={toParam || todayIsoDate()}
            onChange={(e) => setCustomRange(e.target.value, toParam || todayIsoDate())}
            className="hv-input text-xs"
            data-testid="plant-uptime-from"
          />
          <span className="text-[#647087]">→</span>
          <input
            type="date"
            value={toParam || todayIsoDate()}
            min={fromParam || undefined}
            max={todayIsoDate()}
            onChange={(e) => setCustomRange(fromParam || daysAgoIso(30), e.target.value)}
            className="hv-input text-xs"
            data-testid="plant-uptime-to"
          />
          {(fromParam || toParam) && (
            <button
              type="button"
              onClick={() => setPreset("30d")}
              className="hv-btn hv-btn-ghost text-[10px]"
              data-testid="plant-uptime-clear-range"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {err && <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{err}</div>}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]" data-testid="plant-uptime-stats">
        <Stat icon={TrendingUp} label="Uptime" value={fmtPct(stats.uptime_pct)} accent={uptimeAccent(stats.uptime_pct || 0)} />
        <Stat icon={AlertCircle} label="Incidents" value={stats.incidents ?? "—"} accent="#1FB6C1" />
        <Stat icon={Clock} label="MTTR" value={fmtHours(stats.mttr_hours)} accent="#A78BFA" />
        <Stat
          icon={TrendingDown}
          label={stats.currently_down ? "DOWN since" : "State"}
          value={stats.currently_down ? fmtDate(stats.down_since) : "Running"}
          accent={stats.currently_down ? "#F43F5E" : "#10B981"}
        />
      </div>

      {/* Timeline / audit */}
      <div className="hv-card p-3" data-testid="plant-uptime-timeline">
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2">
          Timeline · {events.length} event{events.length === 1 ? "" : "s"} in window
        </div>
        {loading && <div className="text-sm text-[#647087] py-3">Loading…</div>}
        {!loading && events.length === 0 && (
          <div className="text-xs text-[#647087] py-3 italic">
            No down / up transitions in this window.
          </div>
        )}
        {!loading && events.length > 0 && (
          <div className="relative pl-4">
            <div className="absolute left-1.5 top-1 bottom-1 w-px bg-white/10" aria-hidden="true" />
            {events.map((ev) => (
              <EventRow key={ev.id} ev={ev} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="hv-card p-2.5 flex items-center gap-2">
      <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
        <div className="text-sm font-bold truncate" style={{ color: accent }}>{value}</div>
      </div>
    </div>
  );
}

function EventRow({ ev }) {
  const isDown = ev.status === "down";
  return (
    <div className="relative pb-3" data-testid={`plant-uptime-event-${ev.id}`}>
      <div
        className={`absolute -left-[10px] top-1.5 w-2 h-2 rounded-full ${isDown ? "bg-rose-400" : "bg-emerald-400"}`}
        style={{ boxShadow: `0 0 0 2px ${isDown ? "#f43f5e33" : "#10b98133"}` }}
      />
      <div className="text-[11px] flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className={`uppercase tracking-wider font-bold ${isDown ? "text-rose-300" : "text-emerald-300"}`}>
          {ev.status}
        </span>
        <span className="text-[#a0aabf] font-mono text-[10px]">{fmtDate(ev.at)}</span>
        {ev.by_user_name && (
          <span className="text-[#647087] text-[10px]">· by {ev.by_user_name}</span>
        )}
      </div>
      {ev.ticket && (
        <Link
          to={`/tickets/${ev.ticket.id}`}
          className="mt-1 block border border-white/5 rounded p-2 hover:border-[#22d3ee]/40 hover:bg-white/[0.02] transition"
          data-testid={`plant-uptime-event-ticket-${ev.id}`}
        >
          <div className="flex items-center gap-1.5">
            <LifeBuoy className="w-3 h-3 text-rose-300" />
            <span className="text-xs font-semibold">
              #{ev.ticket.short_id} — {ev.ticket.subject}
            </span>
            {ev.ticket.status && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                ev.ticket.status === "closed" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"
              }`}>
                {ev.ticket.status}
              </span>
            )}
            {ev.ticket.priority && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-[#a0aabf]">
                {ev.ticket.priority}
              </span>
            )}
          </div>
          {ev.ticket.description && (
            <div className="mt-1 text-[11px] text-[#a0aabf]">
              <span className="text-[9px] uppercase tracking-wider text-[#647087] mr-1">Reported:</span>
              {ev.ticket.description}
            </div>
          )}
          {/* Iter 418.aw (Dirk, 2026-02-28) — Every ticket note the
              team added inline. Boards want to see the story, not
              just the one-line resolution. */}
          {(ev.ticket.notes || []).map((n) => (
            <div
              key={n.id || n.created_at}
              className="mt-1 text-[11px] text-[#c4cad9] pl-2 border-l-2 border-slate-500/40 bg-white/[0.02] rounded-r py-1 px-2"
            >
              <div className="text-[9px] text-[#647087] font-semibold">
                {n.by_user_name || "—"}
                {n.created_at && <span> · {fmtDate(n.created_at)}</span>}
              </div>
              <div>{n.text}</div>
            </div>
          ))}
          {ev.ticket.resolution_note && (
            <div className="mt-1 text-[11px] text-emerald-300/90 flex items-start gap-1">
              <MessageSquareText className="w-3 h-3 mt-0.5 shrink-0" />
              <span><strong>Resolution:</strong> {ev.ticket.resolution_note}</span>
            </div>
          )}
          {ev.ticket.assigned_to_name && (
            <div className="mt-1 text-[10px] text-[#647087]">Assigned: {ev.ticket.assigned_to_name}</div>
          )}
        </Link>
      )}
      {!ev.ticket && ev.note && (
        <div className="mt-1 text-[11px] text-[#a0aabf] italic">“{ev.note}”</div>
      )}
    </div>
  );
}
